/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Accessibility Provider — Sprint E (WCAG 2.2 AA + Modalità Anziani)
 *
 * Provides:
 *  - elderly mode: increases base font size, adds bold text, larger touch targets
 *  - highContrast: forces AAA-level contrast for elderly / visually impaired
 *  - reduceMotion: honors user's system preference OR forces animations off
 *  - textScale: fine-grained font scale (100 / 115 / 130 / 150)
 *
 * All preferences persist in localStorage. System reduced-motion is auto-detected.
 * Uses classes on <html> so any component can style itself accordingly.
 */

const STORAGE_KEY = 'sc_a11y';

const DEFAULTS = {
  elderlyMode: false,
  highContrast: false,
  reduceMotion: 'auto', // 'auto' | 'always' | 'never'
  textScale: 100,       // 100 | 115 | 130 | 150
};

const AccessibilityContext = createContext({
  ...DEFAULTS,
  setElderlyMode: () => {},
  setHighContrast: () => {},
  setReduceMotion: () => {},
  setTextScale: () => {},
  resetAll: () => {},
});

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
};

export const AccessibilityProvider = ({ children }) => {
  const [state, setState] = useState(readStored);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage might be full or blocked — non-critical */
    }
  }, [state]);

  // Apply classes/attrs to <html>
  useEffect(() => {
    const root = document.documentElement;

    // Elderly mode
    root.classList.toggle('elderly-mode', !!state.elderlyMode);

    // High contrast (independent of elderly mode)
    root.classList.toggle('high-contrast', !!state.highContrast);

    // Text scale: elderly mode forces at least 130
    const effectiveScale = state.elderlyMode
      ? Math.max(state.textScale, 130)
      : state.textScale;
    root.style.setProperty('--text-scale', String(effectiveScale / 100));
    root.setAttribute('data-text-scale', String(effectiveScale));

    // Reduce motion
    let reduce = false;
    if (state.reduceMotion === 'always') reduce = true;
    else if (state.reduceMotion === 'never') reduce = false;
    else {
      // auto: follow system
      reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    root.classList.toggle('reduce-motion', reduce);
  }, [state]);

  // Listen to system reduced-motion changes when in auto
  useEffect(() => {
    if (state.reduceMotion !== 'auto') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => {
      document.documentElement.classList.toggle('reduce-motion', media.matches);
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [state.reduceMotion]);

  const setElderlyMode = useCallback((v) => {
    setState((s) => ({ ...s, elderlyMode: !!v }));
  }, []);

  const setHighContrast = useCallback((v) => {
    setState((s) => ({ ...s, highContrast: !!v }));
  }, []);

  const setReduceMotion = useCallback((v) => {
    const allowed = ['auto', 'always', 'never'];
    setState((s) => ({ ...s, reduceMotion: allowed.includes(v) ? v : 'auto' }));
  }, []);

  const setTextScale = useCallback((v) => {
    const n = Number(v);
    const allowed = [100, 115, 130, 150];
    setState((s) => ({ ...s, textScale: allowed.includes(n) ? n : 100 }));
  }, []);

  const resetAll = useCallback(() => {
    setState({ ...DEFAULTS });
  }, []);

  return (
    <AccessibilityContext.Provider
      value={{
        ...state,
        setElderlyMode,
        setHighContrast,
        setReduceMotion,
        setTextScale,
        resetAll,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => useContext(AccessibilityContext);
